import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Typography, Row, Col, Button, Spin, message, Statistic, Space, Tooltip, Select, Popconfirm, Modal, Form, Input, InputNumber } from 'antd';
import { ReloadOutlined, SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined, CalendarOutlined, UserOutlined, FileTextOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { listUsers } from '../services/userService';
import { getSalarySlip, listSalarySlipsForMonth, listSalarySlipsForUser, regenerateSalarySlipPdf } from '../services/salarySlipService';
import { getAdvanceHistoryForMonth } from '../services/advanceService';
import { themeConfig } from '../config/theme';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title } = Typography;

const UserSalarySlips = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [slips, setSlips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [advanceHistory, setAdvanceHistory] = useState([]);
    const [regeneratingPdfs, setRegeneratingPdfs] = useState(new Set());

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await listUsers();
                setUsers(response.users || []);
            } catch (error) {
                messageApi.error('Failed to fetch users');
            }
        };
        fetchUsers();
    }, []);

    const handleFetchSlips = async () => {
        if (!selectedUser) {
            messageApi.warn('Please select a user first.');
            return;
        }
        setLoading(true);
        try {
            const data = await listSalarySlipsForUser(selectedUser);
            console.log(data);
            setSlips(data.salarySlips || []);
        } catch (error) {
            messageApi.error('Failed to fetch salary slips.');
        } finally {
            setLoading(false);
        }
    };

    const handleShowAdvanceHistory = async (record) => {
        setModalVisible(true);
        setModalLoading(true);
        try {
            const [year, month] = record.MonthYear.split('-');
            const { data } = await getAdvanceHistoryForMonth(selectedUser, year, month);
            setAdvanceHistory(data.advances || []);
        } catch (error) {
            messageApi.error("Failed to load advance history.");
        } finally {
            setModalLoading(false);
        }
    };

    const handleRegeneratePdf = async (record) => {
        const recordKey = record.SK;
        setRegeneratingPdfs(prev => new Set(prev).add(recordKey));
        
        try {
            const [year, month] = record.MonthYear.split('-');
            await regenerateSalarySlipPdf(selectedUser, year, month);
            messageApi.success('PDF regenerated successfully!');
            // Refresh the slips to get updated PDF URL
            handleFetchSlips();
        } catch (error) {
            messageApi.error('Failed to regenerate PDF. Please try again.');
        } finally {
            setRegeneratingPdfs(prev => {
                const newSet = new Set(prev);
                newSet.delete(recordKey);
                return newSet;
            });
        }
    };

    const columns = [
        { title: 'Month', dataIndex: 'MonthYear', key: 'month', sorter: (a, b) => a.MonthYear.localeCompare(b.MonthYear), defaultSortOrder: 'descend' },
        { title: 'Gross Salary', dataIndex: 'GrossSalary', key: 'gross', render: (val) => `₹${val.toFixed(2)}` },
        { 
            title: 'Advance Deducted', 
            dataIndex: 'AdvanceDeductedThisMonth', 
            key: 'deduction', 
            render: (val, record) => (
                <span>
                    ₹{val.toFixed(2)}{' '}
                    <InfoCircleOutlined 
                        style={{ cursor: 'pointer', color: themeConfig.status.info }} 
                        onClick={() => handleShowAdvanceHistory(record)}
                    />
                </span>
            )
        },
        { title: 'Net Salary Paid', dataIndex: 'NetSalaryPaid', key: 'net', render: (val) => `₹${val.toFixed(2)}` },
        { title: 'Shifts Worked', dataIndex: 'TotalShiftsWorked', key: 'shifts' },
        { title: 'Rate', dataIndex: 'ApplicableRate', key: 'rate', render: (val) => `₹${val.toFixed(2)}` },
        { title: 'Advance C/F', dataIndex: 'AdvanceCarriedForward', key: 'cf', render: (val) => `₹${val.toFixed(2)}` },
        { title: 'Generated At', dataIndex: 'GeneratedAt', key: 'timestamp', render: (ts) => dayjs(ts).format('YYYY-MM-DD HH:mm') },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        onClick={() => window.open(record.pdfUrl, '_blank')}
                        disabled={!record.pdfUrl}
                        icon={<FileTextOutlined />}
                    >
                        Download
                    </Button>
                    <Button
                        type="default"
                        onClick={() => handleRegeneratePdf(record)}
                        icon={<ReloadOutlined />}
                        loading={regeneratingPdfs.has(record.SK)}
                        disabled={regeneratingPdfs.has(record.SK)}
                    >
                        Regenerate PDF
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {contextHolder}
            <Title level={3}>User Salary Slip History</Title>
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col>
                    <Select
                        showSearch
                        placeholder="Select a user"
                        style={{ width: 250 }}
                        value={selectedUser}
                        onChange={setSelectedUser}
                        optionFilterProp="children"
                    >
                        {users.map((user) => (
                            <Option key={user.MobileNumber} value={user.MobileNumber}>
                                {user.FullName} ({user.Job})
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col>
                    <Button type="primary" onClick={handleFetchSlips} loading={loading}>
                        Load Slips
                    </Button>
                </Col>
            </Row>

            <Spin spinning={loading}>
                <Table
                    columns={columns}
                    dataSource={slips}
                    rowKey="SK"
                    pagination={{ pageSize: 12 }}
                    scroll={{ x: 'max-content' }}
                />
            </Spin>

            <Modal
                title="Monthly Advance History"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[<Button key="close" onClick={() => setModalVisible(false)}>Close</Button>]}
            >
                <Spin spinning={modalLoading}>
                    <Table 
                        dataSource={advanceHistory} 
                        columns={[
                            { title: 'Date', dataIndex: 'Timestamp', render: (ts) => dayjs(ts).format('YYYY-MM-DD') },
                            { title: 'Amount', dataIndex: 'Amount', render: (amt) => `₹${amt.toFixed(2)}` }
                        ]} 
                        pagination={false} 
                        rowKey="SK"
                    />
                </Spin>
            </Modal>
        </div>
    );
};

export default UserSalarySlips; 